import type { ComponentChildren, ComponentType } from 'preact';
import type { LucideProps } from 'lucide-preact';
import { Icon } from '../primitives/Icon';
import { cn } from '@/lib/cn';

export interface EmptyStateProps {
  icon?: ComponentType<LucideProps>;
  title: ComponentChildren;
  description?: ComponentChildren;
  action?: ComponentChildren;
  secondary?: ComponentChildren;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

// Todos los tamaños usan la sans del sistema (Hanken Grotesk). `lg` es el
// hero de página entera: solo más grande y semibold, sin serif decorativa
// (la Instrument Serif chocaba con la estética del resto de la app).
const SIZE = {
  sm: {
    wrap: 'py-12',
    iconSize: 28,
    title: 'text-sm',
    desc: 'text-xs leading-relaxed',
    gap: 'gap-3',
    halo: 56,
  },
  md: { wrap: 'py-16', iconSize: 36, title: 'text-base', desc: 'text-sm', gap: 'gap-4', halo: 72 },
  lg: {
    wrap: 'py-20',
    iconSize: 44,
    title: 'text-xl font-semibold tracking-tight',
    desc: 'text-sm',
    gap: 'gap-4',
    halo: 88,
  },
} as const;

export function EmptyState({
  icon,
  title,
  description,
  action,
  secondary,
  size = 'md',
  className,
}: EmptyStateProps) {
  const s = SIZE[size];
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        s.wrap,
        s.gap,
        className,
      )}
    >
      {icon ? (
        // Refresh 2026-05-20: el icono se ancla sobre un "halo" radial muy
        // sutil del brand color.  Da un punto de presencia visual sin pesar
        // (es solo un radial gradient en bg) y diferencia el empty state
        // moderno del "icono solo gris" de antes.  Se ve estilo Linear /
        // Notion: la página vacía siente vivacidad, no esterilidad.
        <div
          className="relative flex shrink-0 items-center justify-center"
          style={{ width: s.halo, height: s.halo }}
          aria-hidden="true"
        >
          <span
            className="absolute inset-0 rounded-full"
            style={{
              background: 'radial-gradient(circle, var(--brand-tint) 0%, transparent 70%)',
            }}
          />
          <Icon as={icon} size={s.iconSize} className="text-fg-faint relative" strokeWidth={1.5} />
        </div>
      ) : null}
      <div className="flex flex-col gap-1">
        <p className={cn('text-fg-strong font-medium', s.title)}>{title}</p>
        {description ? (
          <p className={cn('text-fg-muted max-w-sm leading-6', s.desc)}>{description}</p>
        ) : null}
      </div>
      {(action || secondary) && (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
          {action}
          {secondary}
        </div>
      )}
    </div>
  );
}
